<script lang="ts" setup>
import { i18n } from '@/i18n';
import { OrderGroupMenuItems, ORDER_GROUP_MENU_HEIGHT } from '../../constants';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { watch } from 'vue';
import TextIconButton from '@/corePagesModule/components/TextIconButton.vue';
import { useOrderTableStore } from '../../pages/useOrderTableStore';

const t = i18n.t;
const isOpen = ref(false);
const orderGroupContainerRef = ref<HTMLDivElement | null>(null);
const orderGroupDropdownRef = ref<HTMLDivElement | null>(null);

const store = useOrderTableStore();
const { state } = store;

const isOrderGroupAddAvailable = computed(() => state.checkedIds.length > 0);

watch(isOpen, (newVal) => {
  if (newVal) {
    calculateDropdownPosition();
  }
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value || !orderGroupContainerRef.value || !orderGroupDropdownRef.value) return;
  const path = event.composedPath() as EventTarget[];
  if (!(path.includes(orderGroupContainerRef.value) || path.includes(orderGroupDropdownRef.value))) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.addEventListener('click', closeDropdown);
});

/**
 * ドロップダウンの位置を計算し、設定します。
 */
const calculateDropdownPosition = () => {
  nextTick(() => {
    if (!(orderGroupContainerRef.value && orderGroupDropdownRef.value)) return;
    const rect = orderGroupContainerRef.value.getBoundingClientRect();
    const dropdown = orderGroupDropdownRef.value;
    dropdown.style.left = `${rect.left}px`;
    dropdown.style.top = `${rect.bottom}px`;

    // ドロップダウンの高さを計算
    const desiredHeight = ORDER_GROUP_MENU_HEIGHT * Object.keys(OrderGroupMenuItems).length;

    // max-heightを計算して設定
    const availableHeight = window.innerHeight - rect.bottom - 10; // 10pxは少しのマージン
    dropdown.style.maxHeight = `${Math.min(desiredHeight, availableHeight)}px`;
  });
};

/**
 * 選択肢をクリックした場合の処理
 * @param option - 選択された項目
 */
const onClickOption = async (option: OrderGroupMenuItems) => {
  isOpen.value = false;
  state.orderGroupState.openingModal = option;
};
</script>

<template>
  <div class="order-group-menu_container" @click="closeDropdown($event)">
    <div ref="orderGroupContainerRef" class="order-group-menu">
      <TextIconButton
        :text="t('発注グループ')"
        :icon="isOpen ? 'arrowUpBlue' : 'arrowDownBlue'"
        color-style="secondary"
        @click="toggleDropdown"
      />
      <Teleport v-if="isOpen" to="body">
        <div ref="orderGroupDropdownRef" class="order-group-menu__dropdown">
          <button
            v-for="(option, key) in OrderGroupMenuItems"
            :key="key"
            class="order-group-menu__option"
            :class="{ 'order-group-menu__option--disabled': option === OrderGroupMenuItems.OrderGroupAdd && !isOrderGroupAddAvailable }"
            :value="option"
            :disabled="option === OrderGroupMenuItems.OrderGroupAdd && !isOrderGroupAddAvailable"
            @click="() => onClickOption(option)"
          >
            {{ t(option) }}
          </button>
        </div>
      </Teleport>
    </div>
  </div>
</template>
<style scoped lang="scss">
.order-group-menu {
  justify-items: center;
  position: relative;
  user-select: none;
  box-sizing: border-box;
  font-family: Noto Sans JP;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 0;
  }

  &__field {
    height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0em;
    color: $semanticPrimary;
    background-color: $semanticWhite;
  }

  &__dropdown {
    width: fit-content;
    position: fixed;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050; // モーダルより高い値に設定
    overflow-y: auto;
    max-height: 300px; // 適切な最大高さを設定
    box-sizing: border-box;
  }

  &__option {
    width: 100%;
    padding: 10px;
    display: block;

    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;

    min-height: 32px;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}

:deep(.order-group-create-modal-content) {
  background-color: $backgroundDefault;
  padding: 16px;
}
</style>
